import { useEffect, useMemo } from "react";
import { getTheme, DEFAULT_THEME_NAME } from "../themes";
import { ThemeContext } from "../themes/ThemeContext";

// Read the theme name from a URL query param, e.g. ?theme=locationname.
// WS3 (embeddable snippet) will override this with a prop.
function resolveThemeName(propName) {
  if (propName) return propName;
  if (typeof window !== "undefined") {
    try {
      const params = new URLSearchParams(window.location.search);
      const fromUrl = params.get("theme");
      if (fromUrl) return fromUrl;
    } catch (_) {
      // ignore — fall through to default
    }
  }
  return DEFAULT_THEME_NAME;
}

function buildCssVars(theme) {
  const c = theme.colors || {};
  const fUi = theme.fonts?.ui?.family;
  const fAccent = theme.fonts?.accent?.family;
  return {
    "--axify-color-primary-dark": c.primaryDark,
    "--axify-color-brand-mid": c.brandMid,
    "--axify-color-background": c.background,
    "--axify-color-surface": c.surface,
    "--axify-color-border": c.border,
    "--axify-color-cta": c.cta,
    "--axify-color-online-dot": c.onlineDot,
    "--axify-color-text-primary": c.textPrimary,
    "--axify-color-text-on-primary": c.textOnPrimary,
    // Legacy variables — kept so existing rules referencing them theme correctly
    "--axify-widget-text": c.textPrimary,
    "--axify-widget-background": c.background,
    "--axify-widget-axify-darkblue": c.legacyDarkBlue || c.primaryDark,
    "--axify-widget-axify-lime": c.legacyLime || c.onlineDot,
    "--axify-font-ui": fUi,
    "--axify-font-accent": fAccent,
  };
}

// data-axify-theme-font attribute lets us safely remove only our injected links
const FONT_LINK_ATTR = "data-axify-theme-font";

function syncFontLinks(theme) {
  if (typeof document === "undefined") return () => {};

  const uiHref = theme.fonts?.ui?.googleFontsHref;
  const accentHref = theme.fonts?.accent?.googleFontsHref;
  const hrefs = [uiHref, accentHref].filter(Boolean);

  const created = [];
  hrefs.forEach((href) => {
    const existing = document.querySelector(
      `link[${FONT_LINK_ATTR}][href="${href}"]`
    );
    if (existing) return;
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = href;
    link.setAttribute(FONT_LINK_ATTR, theme.name || "");
    document.head.appendChild(link);
    created.push(link);
  });

  return () => {
    created.forEach((link) => {
      if (link.parentNode) link.parentNode.removeChild(link);
    });
  };
}

function ThemeProvider({
  themeName,
  forceLayout,
  children,
  className,
  style,
  ...rest
}) {
  const resolvedName = useMemo(() => resolveThemeName(themeName), [themeName]);
  const baseTheme = useMemo(() => getTheme(resolvedName), [resolvedName]);
  // forceLayout (e.g. embed mode forcing "compact") overrides the theme's
  // own layout without changing colours/fonts.
  const theme = useMemo(
    () => (forceLayout ? { ...baseTheme, layout: forceLayout } : baseTheme),
    [baseTheme, forceLayout]
  );
  const cssVars = useMemo(() => buildCssVars(theme), [theme]);

  useEffect(() => syncFontLinks(theme), [theme]);

  return (
    <ThemeContext.Provider value={theme}>
      <div
        className={className}
        data-axify-theme={theme.name}
        data-axify-layout={theme.layout || "classic"}
        style={{ ...cssVars, ...(style || {}) }}
        {...rest}
      >
        {children}
      </div>
    </ThemeContext.Provider>
  );
}

export default ThemeProvider;
